import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { env } from '../config/env';
import { getTeamsToken, isInTeams } from '../teams/teamsContext';

/** Iframe-/Fenster-Timeouts der stillen Erneuerung — interaktiver Fallback nötig. */
const SILENT_TIMEOUT_CODES = new Set([
  'monitor_window_timeout',
  'monitor_token_acquisition_timeout',
  'block_iframe_reload',
  'iframe_closed_prematurely',
]);

/**
 * Liefert eine Funktion, die ein Access-Token für die M365 Adminbox-API beschafft.
 * Innerhalb von Teams wird zuerst Teams-SSO versucht, sonst MSAL
 * (silent, mit Popup-Fallback bei Interaktionsbedarf).
 */
export function useApiToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();

  return useCallback(async () => {
    if (isInTeams()) {
      const teamsToken = await getTeamsToken();
      if (teamsToken) return teamsToken;
    }

    const account = accounts[0] ?? instance.getActiveAccount() ?? undefined;
    try {
      const result = await instance.acquireTokenSilent({
        scopes: [env.apiScope],
        account,
      });
      return result.accessToken;
    } catch (error) {
      // Bei Interaktionsbedarf ODER wenn die stille Iframe-Erneuerung in ein Timeout
      // läuft (z. B. blockierte Drittanbieter-Cookies), interaktiv per Popup nachholen.
      const needsInteraction =
        error instanceof InteractionRequiredAuthError ||
        (error instanceof BrowserAuthError && SILENT_TIMEOUT_CODES.has(error.errorCode));
      if (needsInteraction) {
        const result = await instance.acquireTokenPopup({ scopes: [env.apiScope], account });
        return result.accessToken;
      }
      throw error;
    }
  }, [instance, accounts]);
}
